'use client';

import type {
  SeedingDetailProduct,
  SeedingFeaturedComment,
} from '@/app/(activity)/campaigns/[slug]/types';
import { getSeedingProductImageUrl } from '@/app/(activity)/campaigns/[slug]/pages/SeedingPage/utils/getSeedingMediaUrl';
import Image from '@/components/Image';
import MagicJump, { getJumpUrl, type JumpTarget } from '@/components/MagicJump';
import SwiperNavigationButtons from '@/components/SwiperNavigation';
import { useMergedRefs } from '@/hooks/useMergedRefs';
import { useReporting } from '@/hooks/useReporting';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { HOME_PREVIEW_PATH } from '@/utils/constants';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import { RightArrowFineOutlined } from '@zeelool/icon';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import HomeSectionHeading2026 from '../HomeSectionHeading2026/HomeSectionHeading2026';
import type {
  MaterialGallery2026Props,
  MaterialGallery2026TrackingParams,
  MaterialGallery2026ValidExposureParams,
  MaterialGalleryItem,
} from './types';
import { useMaterialGalleryDetail, useMaterialGallerySummary } from './useMaterialGalleryData';
import {
  buildGalleryPageTrackingParams,
  buildMaterialTrackingParams,
  buildSeedingSummaryMap,
  getMaterialShopSkus,
  getValidMaterialItems,
  normalizeConfiguredText,
} from './utils';

const COMPONENT_ID = 'materialGallery2026';
const DEFAULT_HEADING_COLOR = '#1A1C1E';
const MATERIAL_DETAIL_POPUP_TYPE = '素材对应互动详情弹窗';
const REVIEW_IMAGE_ALT_MAX_LENGTH = 70;

const loadSeedingItemDialog = () =>
  import('@/app/(activity)/campaigns/[slug]/pages/SeedingPage/components/SeedingItemDialog');
const SeedingItemDialog = dynamic(loadSeedingItemDialog, { ssr: false });

interface SelectedMaterial {
  itemId: number;
  originalIndex: number;
}

function preloadSeedingItemDialog() {
  if (typeof window !== 'undefined') {
    void loadSeedingItemDialog();
  }
}

// 首页装修埋点没有收藏事件类型；收藏业务仍由 Wish 自身处理，避免误报商品跳转。
function ignoreMaterialGalleryCollectTracking() {
  return undefined;
}

function buildValidExposureParams(
  trackingParams: MaterialGallery2026TrackingParams,
): MaterialGallery2026ValidExposureParams {
  return {
    module_index: trackingParams.module_index,
    module_id: trackingParams.module_id,
    module_title: trackingParams.module_title || '',
    module_name: trackingParams.module_name,
    module_tab_name: '',
    mkt_index: trackingParams.mkt_index,
    mkt_index_sub: '',
    mkt_jump_type: trackingParams.mkt_jump_type,
    mkt_jump_content: trackingParams.mkt_jump_content,
  };
}

function useMaterialTracking({
  trackingParams,
  validExposureParams,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: Pick<MaterialGallery2026Props, 'onTrack' | 'onExpTrack' | 'onValidExpTrack'> & {
  trackingParams: MaterialGallery2026TrackingParams;
  validExposureParams: MaterialGallery2026ValidExposureParams;
}) {
  const [exposureRef, inView] = useInView({ threshold: 0.8, triggerOnce: true });

  useEffect(() => {
    if (!inView || !onExpTrack) return;

    try {
      onExpTrack(trackingParams);
    } catch (error) {
      logger.error('2026 PC material gallery exposure tracking error:', error);
    }
  }, [inView, onExpTrack, trackingParams]);

  const handleClick = useCallback(() => {
    if (!onTrack) return;

    try {
      onTrack(validExposureParams);
    } catch (error) {
      logger.error('2026 PC material gallery click tracking error:', error);
    }
  }, [onTrack, validExposureParams]);

  const { ref: validExposureRef } = useValidExposure({
    callback: () => onValidExpTrack?.(validExposureParams),
  });

  return {
    cardRef: useMergedRefs(exposureRef, validExposureRef),
    handleClick,
  };
}

function MaterialProductPreview({ product }: { product?: SeedingDetailProduct }) {
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const tSeeding = useTranslations('activity.campaigns.seeding');
  const productDisplayName = product?.short_name || product?.name || '';
  const productImage = getSeedingProductImageUrl(product?.image);
  const frameColor = product?.product_frame_color_name?.trim() || '';

  const { sellPrice, originalPrice } = useMemo(() => {
    if (!product) return { sellPrice: '', originalPrice: '' };
    const resolvedPrice = priceInfo(userInfo, product, currency);
    const sell = resolvedPrice.sell_price || product.price || '';
    const origin = resolvedPrice.price || '';
    const hasDiscount = origin && sell && origin !== sell;

    return {
      sellPrice: sell ? formatPriceNumberWithSymbol(String(sell), currency) : '',
      originalPrice: hasDiscount ? formatPriceNumberWithSymbol(String(origin), currency) : '',
    };
  }, [currency, product, userInfo]);

  if (!product) {
    return (
      <div className="relative w-full overflow-hidden rounded-[8px] p-[8px]" aria-hidden>
        <span className="pointer-events-none absolute inset-0 rounded-[inherit] bg-[rgba(255,255,255,0.7)] backdrop-blur-[12px] backdrop-saturate-150" />
        <div className="relative flex animate-pulse items-center gap-[12px]">
          <div className="size-[88px] rounded-[8px] bg-auxiliary-light-gray" />
          <div className="flex flex-1 flex-col gap-[16px]">
            <div className="h-[16px] w-4/5 rounded bg-auxiliary-light-gray" />
            <div className="h-[18px] w-2/5 rounded bg-auxiliary-light-gray" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full overflow-hidden rounded-[8px] p-[8px]">
      <span
        data-material-glass
        className="pointer-events-none absolute inset-0 rounded-[inherit] bg-[rgba(255,255,255,0.7)] backdrop-blur-[12px] backdrop-saturate-150"
      />
      <div className="relative flex items-center gap-[12px]">
        {productImage ? (
          <div className="flex size-[88px] shrink-0 items-center justify-center overflow-hidden rounded-[8px] bg-primary-white">
            <Image
              src={productImage}
              alt={productDisplayName}
              width={140}
              height={140}
              className="size-[70px] rounded-[8px] object-contain mix-blend-multiply"
            />
          </div>
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col gap-[16px]">
          {productDisplayName || frameColor ? (
            <div className="flex min-w-0 flex-col gap-[2px]">
              {productDisplayName ? (
                <p className="truncate font-bb text-[16px] leading-[19px] text-primary-text">
                  {productDisplayName}
                </p>
              ) : null}
              {frameColor ? (
                <p className="truncate font-br text-[12px] leading-[14px] text-primary-text">
                  {tSeeding('color')}: {frameColor}
                </p>
              ) : null}
            </div>
          ) : null}
          {sellPrice || originalPrice ? (
            <div className="flex items-center gap-[6px] whitespace-nowrap">
              {sellPrice ? (
                <p className="font-bb text-[16px] leading-[19px] text-primary-text">{sellPrice}</p>
              ) : null}
              {originalPrice ? (
                <p className="font-br text-[14px] leading-[17px] text-[#6c6c6c] line-through">
                  {originalPrice}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function MaterialGalleryCard({
  item,
  comment,
  detailProduct,
  detailError,
  isActive,
  imageSizes,
  originalIndex,
  componentId,
  moduleId,
  moduleTitle,
  moduleIndex,
  onIntent,
  onIntentEnd,
  onOpen,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: {
  item: MaterialGalleryItem;
  comment?: SeedingFeaturedComment | null;
  detailProduct?: SeedingDetailProduct;
  detailError?: Error;
  isActive: boolean;
  imageSizes: string;
  originalIndex: number;
  componentId: string;
  moduleId: string;
  moduleTitle: string;
  moduleIndex: number;
  onIntent: (itemId: number) => void;
  onIntentEnd: () => void;
  onOpen: (itemId: number, originalIndex: number) => void;
} & Pick<MaterialGallery2026Props, 'onTrack' | 'onExpTrack' | 'onValidExpTrack'>) {
  const itemId = Number(item.id);
  const accessibleName = item.kol_name?.trim() || item.shop_sku?.trim() || '';
  const commentText = comment?.content?.trim() || '';
  const imageAlt =
    Array.from(commentText).slice(0, REVIEW_IMAGE_ALT_MAX_LENGTH).join('') || accessibleName;
  const trackingParams = useMemo(
    () =>
      buildMaterialTrackingParams({
        item,
        originalIndex,
        componentId,
        moduleId,
        moduleTitle,
        moduleIndex,
        product: detailProduct,
      }),
    [componentId, detailProduct, item, moduleId, moduleIndex, moduleTitle, originalIndex],
  );
  const validExposureParams = buildValidExposureParams(trackingParams);
  const { cardRef, handleClick } = useMaterialTracking({
    trackingParams,
    validExposureParams,
    onTrack,
    onExpTrack,
    onValidExpTrack,
  });

  const handleOpen = useCallback(() => {
    preloadSeedingItemDialog();
    handleClick();
    onOpen(itemId, originalIndex);
  }, [handleClick, itemId, onOpen, originalIndex]);

  const handleIntent = useCallback(() => {
    preloadSeedingItemDialog();
    onIntent(itemId);
  }, [itemId, onIntent]);

  return (
    <div ref={cardRef} className="h-full min-w-0">
      <article className="flex min-w-0 flex-col gap-[8px]">
        <button
          type="button"
          aria-label={accessibleName || undefined}
          data-material-id={itemId}
          className="group relative aspect-[474/592] w-full cursor-pointer overflow-hidden rounded-[12px] bg-auxiliary-light-gray text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-purple"
          onPointerEnter={handleIntent}
          onPointerLeave={onIntentEnd}
          onFocus={handleIntent}
          onBlur={onIntentEnd}
          onClick={handleOpen}
        >
          <Image
            src={item.jump_image || ''}
            alt={imageAlt}
            width={474}
            height={592}
            loading="lazy"
            sizes={imageSizes}
            className="absolute inset-0 h-full w-full object-cover"
          />

          {isActive && !detailError ? (
            <div className="absolute inset-0 flex items-end p-[16px]">
              <MaterialProductPreview product={detailProduct} />
            </div>
          ) : null}
        </button>

        <div className="min-h-[34px] px-[6px] py-[8px]">
          {commentText ? (
            <p className="line-clamp-2 whitespace-pre-line break-words font-br text-[14px] leading-[17px] text-[#151618]">
              {commentText}
            </p>
          ) : null}
        </div>
      </article>
    </div>
  );
}

function MaterialGalleryContent({
  indexData,
  moduleIndex,
  validItems,
  pageId,
  onTrack,
  onBtnTrack,
  onExpTrack,
  onValidExpTrack,
}: MaterialGallery2026Props & {
  validItems: ReturnType<typeof getValidMaterialItems>;
  pageId: number;
}) {
  const [hoveredItemId, setHoveredItemId] = useState<number | null>(null);
  const [selectedMaterial, setSelectedMaterial] = useState<SelectedMaterial | null>(null);
  const { trackEvent } = useReporting();
  const tSeeding = useTranslations('activity.campaigns.seeding');
  const navigationId = useId().replace(/:/g, '');
  const navigationPrefix = `material-gallery-${navigationId}`;
  const isPreview =
    typeof window !== 'undefined' && window.location.pathname.includes(HOME_PREVIEW_PATH);
  const [summaryRef, shouldLoadSummary] = useInView({
    rootMargin: '400px 0px',
    triggerOnce: true,
  });
  const activeItemId = selectedMaterial?.itemId ?? hoveredItemId;
  const shopSkus = useMemo(() => getMaterialShopSkus(validItems), [validItems]);
  const { data: summaryData } = useMaterialGallerySummary(
    pageId,
    shopSkus,
    isPreview,
    shouldLoadSummary,
  );
  const {
    data: detail,
    error: detailError,
    isLoading: detailLoading,
  } = useMaterialGalleryDetail(pageId, activeItemId, isPreview);
  const summaryMap = useMemo(
    () => buildSeedingSummaryMap(summaryData?.items),
    [summaryData?.items],
  );

  const componentId = indexData.component_id || COMPONENT_ID;
  const moduleId = String(indexData.blocks_advances_id || '');
  const configuredTitle = normalizeConfiguredText(indexData.layout_title);
  const configuredSubtitle = normalizeConfiguredText(indexData.layout_subtitle);
  const hasConfiguredHeading = Boolean(configuredTitle || configuredSubtitle);
  const shouldShowConfiguredHeading =
    Number(indexData.layout_title_status) === 1 && hasConfiguredHeading;
  const moduleTitle = configuredTitle;
  const headingTitle = shouldShowConfiguredHeading ? configuredTitle : '';
  const headingSubtitle = shouldShowConfiguredHeading ? configuredSubtitle : '';
  const headingTitleColor = indexData.layout_title_color?.trim() || DEFAULT_HEADING_COLOR;
  const shouldShowHeading = Boolean(headingTitle || headingSubtitle);
  const buttonText = normalizeConfiguredText(indexData.btn_text);
  const viewMoreJump = useMemo<JumpTarget>(() => {
    // 已配置 View More 跳转时，不再被素材所属种草页的 page_url 覆盖。
    if (indexData.jump_type != null || indexData.Jump_target != null) {
      return {
        jump_type: indexData.jump_type,
        Jump_target: indexData.Jump_target,
        product_group: indexData.product_group,
        sku: indexData.sku,
        crowd_recommend: indexData.crowd_recommend,
      };
    }

    return { jump_type: 4, Jump_target: indexData.page_url?.trim() || '' };
  }, [indexData]);
  const viewMoreStatus = indexData.view_more_status ?? indexData.btn_display;
  const shouldShowPageLink = Boolean(
    (viewMoreStatus == null || Number(viewMoreStatus) === 1) &&
      buttonText &&
      getJumpUrl(viewMoreJump),
  );
  const visibleColumnCount = Math.min(validItems.length, 4);
  const canNavigate = validItems.length > visibleColumnCount;
  const imageSizes = `calc(${Number((100 / visibleColumnCount).toFixed(3))}vw - ${
    (96 + (visibleColumnCount - 1) * 12) / visibleColumnCount
  }px)`;

  const selectedEntry = selectedMaterial
    ? validItems.find(({ item }) => Number(item.id) === selectedMaterial.itemId)
    : undefined;
  const isDialogOpen = Boolean(selectedEntry && selectedMaterial);

  useEffect(() => {
    if (!isDialogOpen) return;

    try {
      trackEvent(
        {
          eventName: 'PopUp_Exp',
          properties: {
            popup_type: MATERIAL_DETAIL_POPUP_TYPE,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('2026 PC material gallery popup exposure tracking error:', error);
    }
  }, [isDialogOpen, trackEvent]);

  const handlePageTrack = useCallback(() => {
    try {
      onBtnTrack?.({
        module_index: moduleIndex,
        module_id: moduleId,
        module_title: moduleTitle,
        module_name: componentId,
        btn_name: buttonText,
      });
    } catch (error) {
      logger.error('2026 PC material gallery button click tracking error:', error);
    }

    if (!onTrack) return;

    try {
      onTrack(
        buildValidExposureParams(
          buildGalleryPageTrackingParams({
            componentId,
            moduleId,
            moduleTitle,
            moduleIndex,
            jumpTarget: viewMoreJump,
          }),
        ),
      );
    } catch (error) {
      logger.error('2026 PC material gallery page click tracking error:', error);
    }
  }, [
    buttonText,
    componentId,
    moduleId,
    moduleIndex,
    moduleTitle,
    onBtnTrack,
    onTrack,
    viewMoreJump,
  ]);

  const handleOpen = useCallback((itemId: number, originalIndex: number) => {
    setSelectedMaterial({ itemId, originalIndex });
  }, []);

  const handleClose = useCallback(() => {
    setSelectedMaterial(null);
    setHoveredItemId(null);
  }, []);

  const handleIntentEnd = useCallback(() => {
    setHoveredItemId(null);
  }, []);

  const handleProductClick = useCallback(() => {
    try {
      trackEvent(
        {
          eventName: 'PopUp_Click',
          properties: {
            popup_type: MATERIAL_DETAIL_POPUP_TYPE,
            btn_name: tSeeding('buyNow'),
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('2026 PC material gallery popup click tracking error:', error);
    }

    if (!onTrack || !selectedEntry || !detail?.product || !selectedMaterial) return;

    try {
      onTrack(
        buildValidExposureParams(
          buildMaterialTrackingParams({
            item: selectedEntry.item,
            originalIndex: selectedMaterial.originalIndex,
            componentId,
            moduleId,
            moduleTitle,
            moduleIndex,
            product: detail.product,
          }),
        ),
      );
    } catch (error) {
      logger.error('2026 PC material gallery product click tracking error:', error);
    }
  }, [
    componentId,
    detail?.product,
    moduleId,
    moduleIndex,
    moduleTitle,
    onTrack,
    selectedEntry,
    selectedMaterial,
    tSeeding,
    trackEvent,
  ]);

  return (
    <section
      ref={summaryRef}
      data-component-id={componentId}
      className="swiper-nav-container relative w-full bg-primary-white"
    >
      {shouldShowHeading || shouldShowPageLink ? (
        <HomeSectionHeading2026
          title={headingTitle}
          subtitle={headingSubtitle}
          titleColor={headingTitleColor}
          subtitleColor={
            indexData.layout_subtitle_color || indexData.layout_subtitl_color || undefined
          }
          titleTag="h3"
          subtitleTag="h4"
        >
          {shouldShowPageLink ? (
            <MagicJump
              prefetch
              item={viewMoreJump}
              aria-label={buttonText}
              onBeforeJump={handlePageTrack}
              className="inline-flex cursor-pointer bg-transparent p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-purple"
            >
              <span className="flex items-center gap-[8px] font-br text-[clamp(12px,calc(-4px_+_1.5625vw),16px)] leading-[clamp(14px,calc(-6px_+_1.953125vw),19px)] text-[#1a1c1e]">
                <span>{buttonText}</span>
                <RightArrowFineOutlined className="text-[20px]" aria-hidden />
              </span>
            </MagicJump>
          ) : null}
        </HomeSectionHeading2026>
      ) : null}

      <div className="w-full px-[48px] pb-[24px]">
        <Swiper
          modules={[Navigation]}
          slidesPerView={visibleColumnCount}
          slidesPerGroup={1}
          loop={false}
          spaceBetween={12}
          watchOverflow
          navigation={
            canNavigate
              ? {
                  prevEl: `.${navigationPrefix}-prev`,
                  nextEl: `.${navigationPrefix}-next`,
                }
              : false
          }
          wrapperTag="ul"
          className="!overflow-hidden"
        >
          {validItems.map(({ item, originalIndex }) => {
            const itemId = Number(item.id);
            const summary = summaryMap.get(itemId);
            const isActive = activeItemId === itemId;

            return (
              // Swiper 初始化前 slide 默认为 100% 宽且没有 spaceBetween，首帧先对齐最终布局。
              <SwiperSlide
                key={itemId}
                tag="li"
                className="!h-auto min-w-0 shrink-0"
                style={{ width: imageSizes, marginRight: 12 }}
              >
                <MaterialGalleryCard
                  item={item}
                  comment={summary?.comment}
                  detailProduct={isActive ? detail?.product : undefined}
                  detailError={isActive ? detailError : undefined}
                  isActive={isActive}
                  imageSizes={imageSizes}
                  originalIndex={originalIndex}
                  componentId={componentId}
                  moduleId={moduleId}
                  moduleTitle={moduleTitle}
                  moduleIndex={moduleIndex}
                  onIntent={setHoveredItemId}
                  onIntentEnd={handleIntentEnd}
                  onOpen={handleOpen}
                  onTrack={onTrack}
                  onExpTrack={onExpTrack}
                  onValidExpTrack={onValidExpTrack}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>

      {canNavigate ? (
        <SwiperNavigationButtons
          navigationPrefix={navigationPrefix}
          prevIcon={
            <RightArrowFineOutlined
              className="swiper-nav-icon size-[20px] rotate-180 [&_path]:stroke-white"
              aria-hidden
            />
          }
          nextIcon={
            <RightArrowFineOutlined
              className="swiper-nav-icon size-[20px] [&_path]:stroke-white"
              aria-hidden
            />
          }
          prevButtonPosition="left-[clamp(24px,calc(-40px_+_6.25vw),40px)] 3xl:left-[56px]"
          nextButtonPosition="right-[clamp(24px,calc(-40px_+_6.25vw),40px)] 3xl:right-[56px]"
          prevButtonClassName="!top-[calc(50%+22px)] !z-[400] !size-[32px] !bg-black/30 hover:!scale-100 hover:!bg-black/30 focus-visible:pointer-events-auto focus-visible:opacity-100"
          nextButtonClassName="!top-[calc(50%+22px)] !z-[400] !size-[32px] !bg-black/30 hover:!scale-100 hover:!bg-black/30 focus-visible:pointer-events-auto focus-visible:opacity-100"
        />
      ) : null}

      {selectedEntry && selectedMaterial ? (
        <SeedingItemDialog
          open
          loading={detailLoading}
          detail={activeItemId === selectedMaterial.itemId ? (detail ?? null) : null}
          onClose={handleClose}
          isWap={false}
          index={selectedMaterial.originalIndex}
          trackCampaignPopupCollectClick={ignoreMaterialGalleryCollectTracking}
          trackCampaignPopupClick={handleProductClick}
        />
      ) : null}
    </section>
  );
}

export default function MaterialGallery2026(props: MaterialGallery2026Props) {
  const { indexData } = props;
  const validItems = useMemo(
    () => getValidMaterialItems(indexData.material_list),
    [indexData.material_list],
  );
  const pageId = Number(indexData.page_id);

  if (!Number.isFinite(pageId) || pageId <= 0 || validItems.length === 0) return null;

  return <MaterialGalleryContent key={pageId} {...props} pageId={pageId} validItems={validItems} />;
}
